import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import { AuthContext } from "../../context/Auth/AuthContext";

import TriggerFunnelsModal from "../../components/TriggerFunnelsModal/TriggerFunnelsModal";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
	switch (action.type) {
		case "LOAD_FUNIS":
			return action.payload;
		case "APPEND_FUNIS": {
			const nextItems = action.payload.filter(
				(item) => !state.some((existing) => existing.id === item.id)
			);
			return [...state, ...nextItems];
		}
		case "UPDATE_FUNI":
			const updated = state.filter((f) => f.id !== action.payload.id);
			return [action.payload, ...updated];
		case "DELETE_FUNI":
			return state.filter((f) => f.id !== action.payload);
		default:
			return state;
	}
};

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconEdit({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	);
}

function IconDelete({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function TriggerFunnels() {
	const { user } = useContext(AuthContext);
	const [funnels, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [pageNumber, setPageNumber] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedFunnel, setSelectedFunnel] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [funnelToDelete, setFunnelToDelete] = useState(null);

	useEffect(() => {
		const socket = openSocket({
			scope: "mensagemFunil",
			userId: user.id,
			component: "MensagemFunil",
		});

		socket.on("mensagemFunil", (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_FUNI", payload: data.resultado });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user.id]);

	useEffect(() => {
		setPageNumber(1);
	}, [searchParam]);

	useEffect(() => {
		setLoading(true);

		const delay = setTimeout(() => {
			const load = async () => {
				try {
					const { data } = await api.get("/mensagemfunil", {
						params: { searchParam, pageNumber },
					});
					dispatch({
						type: pageNumber === 1 ? "LOAD_FUNIS" : "APPEND_FUNIS",
						payload: data.registros,
					});
					setHasMore(data.hasMore);
				} catch (err) {
					toastError(err);
				}
				setLoading(false);
			};

			load();
		}, 500);

		return () => clearTimeout(delay);
	}, [searchParam, pageNumber]);

	const handleSearch = (e) => {
		setSearchParam(e.target.value.toLowerCase());
	};

	const handleOpenModal = () => {
		setSelectedFunnel(null);
		setModalOpen(true);
	};

	const handleEdit = (funnel) => {
		setSelectedFunnel(funnel);
		setModalOpen(true);
	};

	const handleDelete = async (id) => {
		try {
			await api.delete(`/mensagemfunil/${id}`);
			dispatch({ type: "DELETE_FUNI", payload: id });
			toast.success("Mensagem removida com sucesso!");
		} catch (err) {
			toastError(err);
		}
		setFunnelToDelete(null);
		setConfirmModalOpen(false);
	};

	return (
		<MainContainer>
			<div className="app-admin-page">
				<ConfirmationModal
					open={confirmModalOpen}
					onClose={setConfirmModalOpen}
					title="Deseja apagar essa mensagem?"
					onConfirm={() => handleDelete(funnelToDelete?.id)}
				>
					Essa ação não pode ser desfeita.
				</ConfirmationModal>

				<TriggerFunnelsModal
					open={modalOpen}
					onClose={() => setModalOpen(false)}
					funnel={selectedFunnel}
				onSaved={(savedItem) => {
					dispatch({ type: "UPDATE_FUNI", payload: savedItem });
					setModalOpen(false);
				}}
				/>

				<div className="app-admin-page__scroll">
					<AppPageTitle title="Mensagens do funil" subtitle="Automação" />

					<div className="mb-5 flex flex-wrap items-center justify-between gap-3">
						<div className="relative w-full md:max-w-xs">
							<input
								type="search"
								className="form-input form-input-sm w-full ps-11"
								placeholder="Pesquisar..."
								value={searchParam}
								onChange={handleSearch}
							/>
							<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
								<IconSearch />
							</div>
						</div>

						<button type="button" className="btn btn-sm bg-primary text-white" onClick={handleOpenModal}>
							<IconPlus className="size-4" />
							Adicionar
						</button>
					</div>

					{loading && funnels.length === 0 ? (
						<div className="app-modal-loading py-16">
							<div className="app-modal-spinner" />
						</div>
					) : funnels.length === 0 ? (
						<div className="card">
							<div className="card-body flex flex-col items-center justify-center gap-2 py-16 text-center">
								<p className="m-0 font-semibold text-default-800 dark:text-default-100">
									Nenhuma mensagem de funil cadastrada
								</p>
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									Crie o primeiro gatilho para começar.
								</p>
							</div>
						</div>
					) : (
						<div className="card">
							<div className="card-body p-0">
								<div className="app-table-scroll">
									<table className="w-full min-w-[760px] table-auto">
										<thead>
											<tr className="bg-default-150">
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Mensagem
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Funil
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Etiquetas
												</th>
												<th className="w-[88px] px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-default-500">
													Ações
												</th>
											</tr>
										</thead>
										<tbody>
											{funnels.map((item) => (
												<tr key={item.id}>
													<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
														<p className="m-0 line-clamp-2 max-w-[420px] whitespace-pre-wrap break-words">
															{item.mensagem}
														</p>
													</td>
													<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
														{item.salesFunnel?.name || "-"}
													</td>
													<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
														{Array.isArray(item.tags) && item.tags.length > 0 ? (
															<div className="flex flex-wrap gap-1">
																{item.tags.map((tag) => (
																	<span key={tag.id} className="inline-flex rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
																		{tag.name}
																	</span>
																))}
															</div>
														) : item.tag?.name ? (
															<span className="inline-flex rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
																{item.tag.name}
															</span>
														) : (
															"-"
														)}
													</td>
													<td className="px-4 py-3 text-right">
														<AppTableActionMenu
															triggerIcon={<IconMore />}
															triggerAriaLabel={`Ações da mensagem ${item.id}`}
															items={[
																{
																	key: "edit",
																	label: "Editar",
																	icon: <IconEdit className="size-3 shrink-0" />,
																	onClick: () => handleEdit(item),
																},
																{
																	key: "delete",
																	label: "Excluir",
																	icon: <IconDelete className="size-3 shrink-0" />,
																	tone: "danger",
																	onClick: () => {
																		setConfirmModalOpen(true);
																		setFunnelToDelete(item);
																	},
																},
															]}
														/>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}

					{funnels.length > 0 && (
						<div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-default-500 md:justify-between">
							<p className="text-sm text-default-500 dark:text-default-400">
								Exibindo <b>{funnels.length}</b> mensagens carregadas
							</p>
							{hasMore && (
								<button
									type="button"
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
									onClick={() => setPageNumber((prev) => prev + 1)}
									disabled={loading}
								>
									{loading ? "Carregando..." : "Carregar mais"}
								</button>
							)}
						</div>
					)}
				</div>
			</div>
		</MainContainer>
	);
}

export default TriggerFunnels;
